import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { usePetsStore } from '../store'
import { EmptyState } from '../../../shared/components/EmptyState'
import { ResponsiveLayout } from '../../../shared/components/layout'
import { StatusCard, Avatar, Badge } from '../../../shared/components/widgets'
import { AlertCircle } from 'lucide-react'
import type { Pet } from '@rondela/shared'

function calculateAge(birthDate: string): string {
  const birth = new Date(birthDate)
  const now = new Date()
  const diffMs = now.getTime() - birth.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays < 30) {
    return `${diffDays} días`
  }

  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths < 12) {
    return `${diffMonths} ${diffMonths === 1 ? 'mes' : 'meses'}`
  }

  const years = Math.floor(diffMonths / 12)
  const remainingMonths = diffMonths % 12

  if (remainingMonths === 0) {
    return `${years} ${years === 1 ? 'año' : 'años'}`
  }

  return `${years} ${years === 1 ? 'año' : 'años'}, ${remainingMonths} ${remainingMonths === 1 ? 'mes' : 'meses'}`
}

function getSpeciesEmoji(especie: string): string {
  return especie === 'perro' ? '🐕' : especie === 'gato' ? '🐈' : '🐾'
}

function PetCard({ pet }: { pet: Pet }) {
  const age = pet.fechaNacimiento ? calculateAge(pet.fechaNacimiento) : null
  const hasMedicalConditions = !!pet.condicionesMedicas

  // Determine status based on medical conditions
  const status = hasMedicalConditions ? 'warning' : 'neutral'

  return (
    <Link to={`/mascotas/${pet.id}`} className="pet-card-link">
      <StatusCard
        status={status}
        stripe={hasMedicalConditions}
        className="pet-list-card"
      >
        <StatusCard.Header>
          <Avatar
            src={pet.fotoUrl}
            fallback={getSpeciesEmoji(pet.especie)}
            size="md"
            ring={hasMedicalConditions}
            ringColor={status}
          />
          <div className="pet-list-card__info">
            <StatusCard.Title>{pet.nombre}</StatusCard.Title>
            {pet.raza && (
              <StatusCard.Subtitle>{pet.raza}</StatusCard.Subtitle>
            )}
          </div>
          <div className="pet-list-card__meta">
            {pet.peso && <Badge variant="pill">{pet.peso} kg</Badge>}
            {age && <Badge variant="time">{age}</Badge>}
            {pet.status === 'inactive' && (
              <Badge variant="status" status="neutral">Inactivo</Badge>
            )}
          </div>
        </StatusCard.Header>
        {hasMedicalConditions && (
          <StatusCard.Content collapsible={false}>
            <div className="pet-list-card__alert">
              <AlertCircle size={14} />
              <span>{pet.condicionesMedicas}</span>
            </div>
          </StatusCard.Content>
        )}
      </StatusCard>
    </Link>
  )
}

export function PetsListPage() {
  const { pets, isLoading, error, fetchPets } = usePetsStore()

  useEffect(() => {
    fetchPets()
  }, [fetchPets])

  if (isLoading && pets.length === 0) {
    return (
      <ResponsiveLayout>
        <div className="page">
          <div className="page-header">
            <h1>Mascotas</h1>
          </div>
          <div className="loading-container">
            <div className="spinner" />
          </div>
        </div>
      </ResponsiveLayout>
    )
  }

  return (
    <ResponsiveLayout>
      <div className="page pets-list-page">
        <div className="page-header">
          <h1>Mascotas</h1>
          <Link to="/mascotas/nueva" className="btn btn-primary">
            Agregar mascota
          </Link>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {pets.length === 0 ? (
          <EmptyState
            title="No hay mascotas"
            description="Agrega tu primera mascota para comenzar a registrar sus cuidados."
            action={
              <Link to="/mascotas/nueva" className="btn btn-primary">
                Agregar mascota
              </Link>
            }
          />
        ) : (
          <div className="pets-list">
            {pets.map((pet) => (
              <PetCard key={pet.id} pet={pet} />
            ))}
          </div>
        )}
      </div>
    </ResponsiveLayout>
  )
}
